import React, { useEffect, useState, useCallback } from 'react';
import { CheckSquare, Square, Plus, Clock, AlertTriangle, CalendarDays } from 'lucide-react';
import crm, { TASK_TYPES } from '../crm/api';
import { buildCalendarUrl, isMeeting, GCAL_ACCOUNT } from '../crm/gcal';

const FILTERS = [
  { key: 'open', label: 'All open', icon: CheckSquare },
  { key: 'today', label: 'Due today', icon: Clock },
  { key: 'overdue', label: 'Overdue', icon: AlertTriangle },
  { key: 'upcoming', label: 'Upcoming', icon: CalendarDays },
  { key: 'done', label: 'Done', icon: CheckSquare },
];

export default function Tasks() {
  const [filter, setFilter] = useState('open');
  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [ttype, setTtype] = useState('call');
  const [ttime, setTtime] = useState('10:00');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    crm.get('/tasks', { params: { filter } }).then(({ data }) => setTasks(data.tasks || [])).catch(() => setTasks([])).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim()) return;
    const calendarUrl = isMeeting(ttype) && due ? buildCalendarUrl({ title, date: due, time: ttime }) : '';
    await crm.post('/tasks', { title, type: ttype, dueDate: due || null, meetingTime: isMeeting(ttype) ? ttime : null, calendarUrl: calendarUrl || null });
    if (calendarUrl) window.open(calendarUrl, '_blank');
    setTitle(''); setDue(''); load();
  };
  const toggle = async (t) => {
    const patch = { done: !t.done };
    if (!t.done) { const o = window.prompt('Outcome / notes for this follow-up (optional):', t.outcome || ''); if (o !== null) patch.outcome = o; }
    await crm.put(`/tasks/${t.id}`, patch); load();
  };
  const del = async (t) => { await crm.delete(`/tasks/${t.id}`); load(); };

  const today = new Date().toISOString().slice(0, 10);
  const overdue = (t) => !t.done && t.dueDate && t.dueDate < today;

  return (
    <div className="max-w-3xl space-y-5">
      {/* quick add */}
      <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux p-4 flex flex-wrap gap-2 items-center">
        <select value={ttype} onChange={(e) => setTtype(e.target.value)} className="px-3 py-2.5 border border-[var(--border)] rounded-xl text-sm bg-[var(--card)]" style={{ color: 'var(--text)' }}>
          {TASK_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="Add a follow-up / task…" className="flex-1 min-w-[180px] px-4 py-2.5 border border-[var(--border)] rounded-xl text-sm bg-transparent focus:ring-2 focus:ring-teal-500 focus:border-transparent" style={{ color: 'var(--text)' }} />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="px-3 py-2.5 border border-[var(--border)] rounded-xl text-sm bg-transparent" style={{ color: 'var(--text)' }} />
        {isMeeting(ttype) && <input type="time" value={ttime} onChange={(e) => setTtime(e.target.value)} className="px-3 py-2.5 border border-[var(--border)] rounded-xl text-sm bg-transparent" style={{ color: 'var(--text)' }} title="Meeting time" />}
        <button onClick={add} className="acc-bg text-white font-bold px-4 py-2.5 rounded-xl flex items-center gap-1.5"><Plus size={16} />Add</button>
      </div>
      {isMeeting(ttype) && <div className="text-[12px] text-[var(--muted)] -mt-2 px-1">📅 Scheduling a {ttype} opens a Google Calendar event (Meet link added on Save) for <b>{GCAL_ACCOUNT}</b>.</div>}

      {/* filters */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const I = f.icon;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold border transition-colors"
              style={filter === f.key ? { background: 'var(--acc)', color: '#fff', borderColor: 'transparent' } : { color: 'var(--muted)', borderColor: 'var(--border)', background: 'var(--card)' }}>
              <I size={15} />{f.label}
            </button>
          );
        })}
      </div>

      {/* list */}
      <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux overflow-hidden">
        {loading ? <div className="p-10 text-center text-[var(--muted)]">Loading…</div>
          : tasks.length === 0 ? <div className="p-10 text-center text-[var(--muted)]">No tasks here.</div>
            : tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-[var(--border)] last:border-0">
                <button onClick={() => toggle(t)} className="shrink-0" style={{ color: t.done ? 'var(--acc)' : 'var(--muted)' }}>
                  {t.done ? <CheckSquare size={20} /> : <Square size={20} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {t.type && t.type !== 'todo' && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0" style={{ background: 'var(--acc-soft)', color: 'var(--acc2)' }}>{t.type}</span>}
                    <div className={`text-sm font-semibold truncate ${t.done ? 'line-through' : ''}`} style={{ color: t.done ? 'var(--muted)' : 'var(--text)' }}>{t.title}</div>
                  </div>
                  <div className="text-[12px] flex items-center gap-2" style={{ color: 'var(--muted)' }}>
                    {t.Lead && <span>🔗 {t.Lead.name}</span>}
                    {t.owner && <span>· {t.owner.name}</span>}
                    {t.done && t.outcome && <span>· 💬 {t.outcome}</span>}
                  </div>
                </div>
                {t.calendarUrl && <a href={t.calendarUrl} target="_blank" rel="noreferrer" className="text-[12px] font-bold text-[var(--acc2)] hover:underline shrink-0" title="Add to Google Calendar">📅 Calendar</a>}
                {t.dueDate && <span className="text-[12px] font-bold px-2.5 py-1 rounded-lg shrink-0" style={overdue(t) ? { color: '#b91c1c', background: '#fee2e2' } : { color: 'var(--muted)', background: 'var(--soft)' }}>{t.dueDate}{t.meetingTime ? ' · ' + t.meetingTime : ''}</span>}
                <button onClick={() => del(t)} className="text-[var(--muted)] hover:text-rose-500 text-lg shrink-0">×</button>
              </div>
            ))}
      </div>
    </div>
  );
}
